import { createColumnHelper } from '@tanstack/react-table'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { RiskProfileAsset } from '../types/riskProfileAssets.types'
import { RiskProfileMultiDamageStates } from '../components/RiskProfileMultiDamageStates'
import { Link } from 'react-router-dom'

const columnHelper = createColumnHelper<RiskProfileAsset>()

function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

function formatRatio(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function formatTopPercentage(percentile: number): string {
  const value = Math.min(100, Math.max(1, Math.ceil(percentile * 100)))
  return `Top ${value}%`
}

export function getRiskProfileAssetsColumns(
  getTitle: (id: string) => string,
  workspaceId: string | null,
  hazardGroups: string[],
  showRank: boolean,
) {
  return [
    columnHelper.display({
      id: 'expander',
      size: 48,
      cell: ({ row }) => {
        const hasDetails = Object.keys(row.original.identifying_information).length > 0
        if (!hasDetails) return null
        return (
          <button
            onClick={row.getToggleExpandedHandler()}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={row.getIsExpanded() ? 'Collapse details' : 'Expand details'}
          >
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )
      },
    }),
    columnHelper.accessor('asset_type', {
      header: 'Asset Type',
      cell: ({ getValue }) => <span className="font-medium">{getTitle(getValue())}</span>,
    }),
    ...(showRank
      ? [
          columnHelper.accessor('asset_rank', {
            header: () => <div className="text-right">Relative Risk Rating</div>,
            cell: ({ getValue, row }) => {
              const rank = getValue()
              if (rank === null || row.original.asset_percentile === null) return null
              return (
                <div className="text-right">
                  {rank}
                  {row.original.asset_rank_tied ? '=' : ''} (
                  {formatTopPercentage(row.original.asset_percentile)})
                </div>
              )
            },
          }),
        ]
      : []),
    columnHelper.accessor('damage_cost', {
      header: () => <div className="text-right">Damage Cost</div>,
      cell: ({ getValue }) => <div className="text-right">{formatCurrency(getValue())}</div>,
    }),
    columnHelper.accessor('replacement_cost', {
      header: () => <div className="text-right">Replacement Cost</div>,
      cell: ({ getValue }) => <div className="text-right">{formatCurrency(getValue())}</div>,
    }),
    columnHelper.accessor('damage_ratio', {
      header: () => <div className="text-right">Damage Ratio</div>,
      cell: ({ getValue }) => <div className="text-right">{formatRatio(getValue())}</div>,
    }),
    columnHelper.accessor('vulnerability', {
      header: 'Damage State',
      cell: ({ getValue, row }) => {
        const highestDamageState = getValue()
        const damageStates = row.original.damage_states
        const rowId = row.id

        return (
          <RiskProfileMultiDamageStates
            rowId={rowId}
            damageStates={damageStates}
            highestDamageState={highestDamageState}
          />
        )
      },
    }),
    columnHelper.display({
      id: 'actions',
      size: 48,
      header: '',
      cell: ({ row }) => {
        const params = new URLSearchParams()
        if (workspaceId) params.set('workspace', workspaceId)
        if (hazardGroups?.length) params.set('hazardGroups', hazardGroups.join(','))
        const asset_type = row.original.asset_type
        const aggregate_id = row.original.aggregate_id

        return (
          <Link
            className="text-xs font-medium text-rexBlue hover:underline"
            to={`/app/risk/profile/element/${asset_type}/${aggregate_id}?${params}`}
          >
            View
          </Link>
        )
      },
    }),
  ]
}
